'use client';

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { springSoft } from '@/lib/motion';
import { formatInt } from '@/lib/formatDate';
import s from './PlatformHeroPanel.module.css';

/**
 * PlatformHeroPanel — shared, data-driven "live dashboard" hero visual for
 * platform pages. Same look across the platform section; each page feeds its
 * own sector data. Animated: live count, sparkline draw, sentiment grow,
 * auto-scrolling feed, floating chip. Reduced-motion safe (CSS guards).
 *
 * Props:
 *   title       header label (e.g. 'Brand Listening', 'Command Centre')
 *   badge       small pill top-right (e.g. '30+ sources')
 *   kpiValue    headline number — animates up (number or numeric string)
 *   kpiLabel    caption under the number
 *   trend       number[] — sparkline points (rising volume)
 *   trendDelta  green pill text (e.g. '+42%')
 *   segments    [{ label, pct, tone }] — stacked bar; tone: pos|neu|neg|brand
 *   feedLabel   ticker heading (e.g. 'Live mentions', 'Incoming alerts')
 *   feed        [{ text, meta, tag, tone, letter? }] — scrolling rows
 *   chip        floating callout text (e.g. 'SignalSense™ · spike +42%')
 */

function sparkPath(points, w, h) {
  if (!points || points.length < 2) return '';
  const max = Math.max(...points);
  const step = w / (points.length - 1);
  return points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${(i * step).toFixed(1)} ${(h - (p / max) * h).toFixed(1)}`)
    .join(' ');
}

export default function PlatformHeroPanel({
  title = 'Live Dashboard',
  badge,
  kpiValue = 0,
  kpiLabel = '',
  trend = [10, 16, 13, 21, 26, 22, 33, 30, 42, 52],
  trendDelta,
  segments = [],
  feedLabel = 'Live feed',
  feed = [],
  chip,
}) {
  const seed = parseInt(String(kpiValue).replace(/[^\d]/g, ''), 10) || 0;
  const suffix = String(kpiValue).replace(/[\d,]/g, '');
  const [count, setCount] = useState(seed);

  useEffect(() => {
    if (!seed) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const id = window.setInterval(() => {
      setCount((n) => n + Math.floor(Math.random() * 9) + 2);
    }, 1700);
    return () => window.clearInterval(id);
  }, [seed]);

  const rows = feed.length ? [...feed, ...feed] : [];
  const letterOf = (item) =>
    (item.letter || (item.meta || item.text || '?').replace(/[^A-Za-z0-9]/g, '').charAt(0) || '•').toUpperCase();

  return (
    <motion.div
      className={s.wrap}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...springSoft, delay: 0.15 }}
    >
      <article className={s.card} aria-label={`${title} preview`}>
        <header className={s.head}>
          <span className={s.liveDot} aria-hidden="true" />
          <span className={s.liveTag}>LIVE</span>
          <span className={s.title}>{title}</span>
          {badge && <span className={s.badge}>{badge}</span>}
        </header>

        <div className={s.metrics}>
          <div className={s.kpi}>
            <span className={s.kpiVal}>{seed ? `${formatInt(count)}${suffix}` : kpiValue}</span>
            <span className={s.kpiLbl}>{kpiLabel}</span>
          </div>
          <div className={s.spark}>
            <svg className={s.sparkSvg} viewBox="0 0 200 56" preserveAspectRatio="none" aria-hidden="true">
              <path className={s.sparkLine} d={sparkPath(trend, 200, 50)} />
            </svg>
            {trendDelta && <span className={s.sparkUp}>▲ {trendDelta}</span>}
          </div>
        </div>

        {segments.length > 0 && (
          <>
            <div className={s.sentiment} aria-hidden="true">
              {segments.map((b, i) => (
                <span key={i} className={s.segment} data-tone={b.tone} style={{ flex: b.pct }} />
              ))}
            </div>
            <div className={s.sentLabels}>
              {segments.map((b, i) => (
                <span key={i} className={s.sentLabel} data-tone={b.tone}>{b.label} {b.pct}%</span>
              ))}
            </div>
          </>
        )}

        {rows.length > 0 && (
          <>
            <div className={s.feedLabel}>{feedLabel}</div>
            <div className={s.feedViewport}>
              <ul className={s.feed}>
                {rows.map((m, i) => (
                  <li key={i} className={s.item}>
                    <span className={s.avatar} data-tone={m.tone} aria-hidden="true">{letterOf(m)}</span>
                    <div className={s.itemText}>
                      <span className={s.itemMsg}>{m.text}</span>
                      {m.meta && <span className={s.itemMeta}>{m.meta}</span>}
                    </div>
                    {m.tag && <span className={s.platform}>{m.tag}</span>}
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </article>

      {chip && (
        <div className={s.chip} aria-hidden="true">
          <span className={s.chipDot} />
          {chip}
        </div>
      )}
    </motion.div>
  );
}
